import { TargetingRule } from '../../uas/editing/targeting/targeting-rule.interface';

export const RulesOperatorMap: Record<string, string> = {
  'not-applicable': '',
  'is-in': 'Is in the list',
  'is-not-in': 'Is not in the list',
  'is-before': 'Is before',
  'is-not-before': 'Is not before',
  'is-after': 'Is after',
  'is': 'Is',
  'is-not': 'Is not',
  'is-between': 'Is between',
  'are-between': 'Are between',
  'is-not-between': 'Is not between',
  'is-marketed-by': 'Marketed by',
  'is-operated-by': 'Operated by',
  'is-connection': 'Is connection',
  'is-not-connection': 'Is not connection',
} as const;

export interface RuleTemplate {
  id: string;
  name: string;
  option_type_ids: string[];
  group?: string;
}

// https://github.com/routehappy/hub/blob/master/lib/hub.rb#L145
export const RulesTemplateMap: Record<string, RuleTemplate> = {
  'equipment-class-flown': {
    id: 'equipment-class-flown',
    name: 'Equipment Class',
    option_type_ids: ['equipment_class', 'equipment_class_list'],
    group: 'Aircraft'
  },
  'arrival-airport': {
    id: 'arrival-airport',
    name: 'Airport',
    option_type_ids: ['airport', 'airport_list', 'connection'],
    group: 'Arrival'
  },
  'arrival-country': {
    id: 'arrival-country',
    name: 'Country',
    option_type_ids: ['country', 'country_list'],
    group: 'Arrival'
  },
  'arrival-or-departure-airport': {
    id: 'arrival-or-departure-airport',
    name: 'Airport',
    option_type_ids: ['airport', 'airport_list'],
    group: 'Departure or Arrival'
  },
  'arrival-or-departure-country': {
    id: 'arrival-or-departure-country',
    name: 'Country',
    option_type_ids: ['country', 'country_list'],
    group: 'Departure or Arrival'
  },
  'domestic': {
    id: 'domestic',
    name: 'Domestic departure and arrival country',
    option_type_ids: ['country', 'country_list'],
  },
  'arrival-time': {
    id: 'arrival-time',
    name: 'Time',
    option_type_ids: ['time', 'time_interval'],
    group: 'Arrival'
  },
  'departure-airport': {
    id: 'departure-airport',
    name: 'Airport',
    option_type_ids: ['airport', 'airport_list'],
    group: 'Departure'
  },
  'departure-country': {
    id: 'departure-country',
    name: 'Country',
    option_type_ids: ['country', 'country_list'],
    group: 'Departure'
  },
  'departure-terminal': {
    id: 'departure-terminal',
    name: 'Terminal',
    option_type_ids: ['terminal', 'terminal_list'],
    group: 'Departure'
  },
  'departure-time': {
    id: 'departure-time',
    name: 'Time',
    option_type_ids: ['time', 'time_interval'],
    group: 'Departure'
  },
  'flight-date': {
    id: 'flight-date',
    name: 'Date',
    option_type_ids: ['date_interval'],
    group: 'Flight'
  },
  'elapsed-time': {
    id: 'elapsed-time',
    name: 'Duration',
    option_type_ids: ['duration_interval'],
    group: 'Flight'
  },
  'arrival-layover-duration': {
    id: 'arrival-layover-duration',
    name: 'Layover duration',
    option_type_ids: ['duration_interval'],
    group: 'Arrival'
  },
  'amenity-layout': {
    id: 'amenity-layout',
    name: 'Layout',
    option_type_ids: ['layout', 'layout_list'],
    group: 'In-flight amenities'
  },
  'amenity-seat': {
    id: 'amenity-seat',
    name: 'Seat',
    option_type_ids: ['seat', 'seat_list'],
    group: 'In-flight amenities'
  },
  'amenity-entertainment': {
    id: 'amenity-entertainment',
    name: 'Entertainment',
    option_type_ids: ['entertainment', 'entertainment_list'],
    group: 'In-flight amenities'
  },
  'amenity-power': {
    id: 'amenity-power',
    name: 'Power',
    option_type_ids: ['power', 'power_list'],
    group: 'In-flight amenities'
  },
  'amenity-food': {
    id: 'amenity-food',
    name: 'Food',
    option_type_ids: ['food', 'food_list'],
    group: 'In-flight amenities'
  },
  'amenity-beverage': {
    id: 'amenity-beverage',
    name: 'Beverage',
    option_type_ids: ['beverage', 'beverage_list'],
    group: 'In-flight amenities'
  },
  'amenity-wifi': {
    id: 'amenity-wifi',
    name: 'Wifi',
    option_type_ids: ['wifi', 'wifi_list'],
    group: 'In-flight amenities'
  },
  'origin-and-destination': {
    id: 'origin-and-destination',
    name: 'Origin & destination',
    option_type_ids: ['origin_and_destination', 'origin_and_destination_list'],
  },
  'subfleet-flown': {
    id: 'subfleet-flown',
    name: 'Equipment Subfleet',
    option_type_ids: ['subfleet', 'subfleet_list'],
    group: 'Aircraft'
  },
  // 'tail-flown': {
  //   'id': 'tail-flown',
  //   'name': 'Tail',
  //   'option_type_ids': [
  //     'tail_list'
  //   ]
  // }
  'codeshare-flight': {
    id: 'codeshare-flight',
    name: 'Codeshare',
    option_type_ids: ['carriers'],
  },
  'not-codeshare': {
    id: 'not-codeshare',
    name: 'Not codeshare',
    option_type_ids: ['not_applicable'],
  },
};

export const MarketRulesTemplateMap = {
  'arrival-airport': {
    id: 'arrival-airport',
    name: 'Arrival airport',
    option_type_ids: ['airport_list_id', 'airport_code'],
  },
  'arrival-country': {
    id: 'arrival-country',
    name: 'Arrival country',
    option_type_ids: ['country_list_id', 'country_code'],
  },
  'departure-airport': {
    id: 'departure-airport',
    name: 'Departure airport',
    option_type_ids: ['airport_list_id', 'airport_code'],
  },
  'departure-country': {
    id: 'departure-country',
    name: 'Departure country',
    option_type_ids: ['country_list_id', 'country_code'],
  },
  'origin-and-destination': {
    id: 'origin-and-destination',
    name: 'Origin & destination',
    option_type_ids: ['origin_and_destination_list_id', 'from'],
  },
};

const transformAmenity: Function = (rule: TargetingRule, values?: string[]) => {
  if (values) {
    rule.display_text_name = values[1];
    rule.display_text_key = values[0];
    rule.option_id = values[0];
  } else {
    rule.option_id = rule.display_text_key;
  }
}

const amenitiesHelpText: string = 'Options will be shown based on selected cabins';
const amenitiesPlaceholderText: string = 'Select display text';
/**
 * This function is needed to initialize equipment_class_value before sending it to the backend.
 * Also, initializez option_id for it to be displayed on the rule component page.
 * @param rule 
 * @param values 
 */
const transformEquipmentClass: Function = (rule: TargetingRule, values?: string[]) => {
  if(values) {
    rule.equipment_class_value = values[0];
    rule.option_id = values[0];  
  }
  else{
    rule.option_id = rule.equipment_class_value;
  }
}

// https://github.com/routehappy/hub/blob/master/lib/hub.rb#L122
export const RulesOptionTypeMap = {
  airport_list: {
    id: 'airport_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  airport: {
    id: 'airport',
    operator_ids: ['is', 'is-not'],
  },
  connection: {
    id: 'connection',
    operator_ids: ['is-connection', 'is-not-connection'],
  },
  country_list: {
    id: 'country_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  country: {
    id: 'country',
    operator_ids: ['is', 'is-not'],
  },
  terminal_list: {
    id: 'terminal_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  terminal: {
    id: 'terminal',
    operator_ids: ['is', 'is-not'],
  },
  time: {
    id: 'time',
    operator_ids: ['is-before', 'is-after'],
  },
  time_interval: {
    id: 'time_interval',
    operator_ids: ['is-between'],
  },
  duration_interval: {
    id: 'duration_interval',
    operator_ids: ['is-between', 'is-not-between'],
  },
  date_interval: {
    id: 'date_interval',
    operator_ids: ['is-between', 'is-not-between'],
  },
  origin_and_destination_list: {
    id: 'origin_and_destination_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  origin_and_destination: {
    id: 'origin_and_destination',
    operator_ids: ['is', 'is-not'],
  },
  subfleet_list: {
    id: 'subfleet_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  subfleet: {
    id: 'subfleet',
    operator_ids: ['is', 'is-not'],
  },
  tail_list: {
    id: 'tail_list',
    operator_ids: ['is-in', 'is-not-in'],
  },
  carriers: {
    id: 'carriers',
    operator_ids: ['is-marketed-by', 'is-operated-by'],
  },
  not_applicable: {
    id: 'not_applicable',
    operator_ids: ['not-applicable'],
  },
  position_list: {
    id: 'position_list',
    operator_ids: ['is', 'is-not'],
  },
  layout: {
    id: 'layout',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  layout_list: {
    id: 'layout_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  seat: {
    id: 'seat',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  seat_list: {
    id: 'seat_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  entertainment: {
    id: 'entertainment',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  entertainment_list: {
    id: 'entertainment_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  power: {
    id: 'power',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  power_list: {
    id: 'power_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  food: {
    id: 'food',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  food_list: {
    id: 'food_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  beverage: {
    id: 'beverage',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  beverage_list: {
    id: 'beverage_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  wifi: {
    id: 'wifi',
    operator_ids: ['is', 'is-not'],
    transform: transformAmenity,
    helpText: amenitiesHelpText,
    placeHolderText: amenitiesPlaceholderText
  },
  wifi_list: {
    id: 'wifi_list',
    operator_ids: ['is-in', 'is-not-in'],
    helpText: amenitiesHelpText,
  },
  //Note - This option type is treated as a list even though the operator_ids are 'is' and 'is-not'
   equipment_class: {
     id: 'equipment_class',
     operator_ids: ['is', 'is-not'],
     transform: transformEquipmentClass
   },
  equipment_class_list: {
    id: 'equipment_class_list',
    operator_ids: ['is-in', 'is-not-in'],
  }
};

export const MarketRulesOptionTypeMap = {
  airport_list_id: {
    id: 'airport_list',
    operand_key: 'airport_list_id',
    operator_ids: ['is-in', 'is-not-in'],
  },
  airport_code: {
    id: 'airport',
    operand_key: 'airport_code',
    operator_ids: ['is', 'is-not'],
  },
  country_list_id: {
    id: 'country_list',
    operand_key: 'country_list_id',
    operator_ids: ['is-in', 'is-not-in'],
  },
  country_code: {
    id: 'country',
    operand_key: 'country_code',
    operator_ids: ['is', 'is-not'],
  },
  origin_and_destination_list_id: {
    id: 'origin_and_destination_list',
    operand_key: 'origin_and_destination_list_id',
    operator_ids: ['is-in', 'is-not-in'],
  },
  from: {
    id: 'origin_and_destination',
    operand_key: 'from',
    operator_ids: ['is', 'is-not'],
  },
};
